import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Input, Modal, Space, Spin, Typography } from 'antd';
import { CopyOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
// Api
import { CHANGE_STREAM_MODE } from 'api/watchstream/mutations';
import { GET_STREAM_BY_ID } from 'api/watchstream/queries';
// Types
import { StreamModeEnum } from 'api/graphql-stream-global-types';
import {
  GetStreamById,
  GetStreamByIdVariables,
} from 'api/watchstream/types/GetStreamById';
import {
  ChangeStreamMode,
  ChangeStreamModeVariables,
} from 'api/watchstream/types/ChangeStreamMode';
// Client
import { streamClient } from 'streamClient';
// Helpers
import { getProperErrorMessage } from 'helpers/errors';
// UI
import { errorNotification, successNotification } from 'ui/Notification';

const { confirm } = Modal;
const { Text } = Typography;

type RtmpSettingsModalProps = {
  streamId: string;
  visible: boolean;
  onClose: () => void;
};

const RtmpSettingsModal = ({
  streamId,
  visible,
  onClose,
}: RtmpSettingsModalProps): JSX.Element => {
  const { data, loading, error } = useQuery<
    GetStreamById,
    GetStreamByIdVariables
  >(GET_STREAM_BY_ID, {
    variables: { id: streamId },
    skip: !visible || !streamId,
    client: streamClient,
    fetchPolicy: 'cache-and-network',
  });

  const [changeStreamMode] = useMutation<
    ChangeStreamMode,
    ChangeStreamModeVariables
  >(CHANGE_STREAM_MODE, {
    client: streamClient,
    refetchQueries: [{ query: GET_STREAM_BY_ID, variables: { id: streamId } }],
    awaitRefetchQueries: true,
  });

  const stream = data?.getStreamById;
  const isOnRtmp = stream?.mode === StreamModeEnum.Rtmp;
  const streamUrl = stream?.rtmpIngestURL || '';
  const streamKey = stream?.rtmpIngestKey || '';
  const hasMissingRtmpCredentials =
    isOnRtmp && (!stream?.rtmpIngestURL || !stream?.rtmpIngestKey);

  const handleCopy = async (text: string) => {
    if (!text.length) {
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      successNotification('Copied');
    } catch {
      errorNotification('Could not copy text');
    }
  };

  // Disable the copy icon when there is no value to copy so the click can't
  // silently no-op.
  const renderCopyAddon = (value: string) => (
    <CopyOutlined
      onClick={() => handleCopy(value)}
      style={
        value
          ? { cursor: 'pointer' }
          : { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' }
      }
    />
  );

  const handleModeChange = (mode: StreamModeEnum) => {
    const isTurningOn = mode === StreamModeEnum.Rtmp;

    confirm({
      title: isTurningOn
        ? 'Are you sure you want to switch this stream to RTMP?'
        : 'Are you sure you want to turn RTMP off for this stream?',
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          await changeStreamMode({
            variables: { id: streamId, mode },
          });
          successNotification(
            isTurningOn
              ? 'The stream has been switched to RTMP successfully'
              : 'RTMP has been turned off successfully'
          );
        } catch (err) {
          errorNotification(
            getProperErrorMessage(err, 'Something went wrong!')
          );
          throw err;
        }
      },
    });
  };

  return (
    <Modal
      title="RTMP Settings"
      visible={visible}
      centered
      width={640}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      {loading && !data ? (
        <Spin />
      ) : error && !data ? (
        <Text type="danger">
          {getProperErrorMessage(
            error,
            'Could not load RTMP settings. Please close and try again.'
          )}
        </Text>
      ) : isOnRtmp ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text>This stream is switched to RTMP</Text>

          {hasMissingRtmpCredentials && (
            <Text type="warning">
              RTMP credentials are not available yet. Close this dialog and open
              it again in a moment.
            </Text>
          )}

          <div>
            <Text strong>Stream URL</Text>
            <Input
              readOnly
              value={streamUrl}
              addonAfter={renderCopyAddon(streamUrl)}
            />
          </div>

          <div>
            <Text strong>Stream Key</Text>
            <Input
              readOnly
              value={streamKey}
              addonAfter={renderCopyAddon(streamKey)}
            />
          </div>

          <Button
            type="primary"
            danger
            onClick={() => handleModeChange(StreamModeEnum.Webrtc)}
          >
            Turn off the RTMP
          </Button>
        </Space>
      ) : (
        <Button
          type="primary"
          onClick={() => handleModeChange(StreamModeEnum.Rtmp)}
        >
          Switch to RTMP
        </Button>
      )}
    </Modal>
  );
};

export default RtmpSettingsModal;
